/*
  File: src/pages/DetectionsHistory.jsx
  Purpose: View historical detections from backend with pagination and optional stream filter.
  Behavior:
  - Fetches GET /detections with offset, limit, optional stream_id
  - Sorts results by stream_id (ascending)
  - Renders image (if metadata.img.url exists) and team/player/number (new + legacy schema)
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Navbar from '../components/layout/Navbar';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { markDetectionAsFalse, markLabelAsFalse } from '../auth/api';
import AddToDataset from '../components/common/AddToDataset';

function extractCustomLabels(metadata) {
    const labels = [];

    // Series format: labels array with 'n' (name) field
    if (Array.isArray(metadata.labels)) {
        metadata.labels.forEach(label => {
            if (label.n) {
                labels.push({
                    name: label.n,
                    confidence: label.c,
                    bbox: label.b,
                    type: 'series',
                    person: label.p, // character/person face
                    personConfidence: label.sp, // person similarity/confidence
                    personBbox: label.bp // character/person bounding box
                });
            }
        });
    }

    // Matches format: teams array with instances containing 'cl' (custom label) field
    if (Array.isArray(metadata.teams)) {
        metadata.teams.forEach(team => {
            if (Array.isArray(team.i)) {
                team.i.forEach(instance => {
                    if (instance.cl) {
                        labels.push({
                            name: instance.cl,
                            confidence: instance.cj, // jersey confidence
                            bbox: instance.bj, // jersey bbox
                            type: 'match',
                            team: team.t,
                            variant: instance.v,
                            side: instance.s
                        });
                    }
                });
            }
        });
    }

    return labels;
}

function classifyInstanceForType(inst) {
    if (!inst || typeof inst !== 'object') return { ivs: 'UNK', db: undefined };
    // Prefer explicit detection_type { db, ivs }
    if (inst.detection_type && typeof inst.detection_type === 'object') {
        const ivs = inst.detection_type.ivs || inst.dt || inst.dt_ivs;
        const db = inst.detection_type.db || inst.dt_db;
        if (ivs) return { ivs, db };
    }
    // Compact code field
    if (typeof inst.dt === 'string' && inst.dt) {
        const ivs = inst.dt;
        const ivsMap = { JF: 'jersey_front_only', JB: 'jersey_back_only', FF: 'jersey_front_face', BNN: 'jersey_back_num_name', BN: 'jersey_back_num_only', BNA: 'jersey_back_name_only' };
        return { ivs, db: ivsMap[ivs] };
    }
    // Heuristic
    const side = String(inst.s || inst.side || '').toLowerCase();
    // number detection present?
    let hasNumber = false;
    const n = inst.n;
    if (n && typeof n === 'object' && !Array.isArray(n)) hasNumber = Boolean(String(n.t || n.text || n.value || '').trim());
    else if (Array.isArray(n) && n.length > 0) {
        const v0 = n[0];
        if (typeof v0 === 'string' || typeof v0 === 'number') hasNumber = true;
        else if (v0 && typeof v0 === 'object') hasNumber = Boolean(String(v0.t || v0.text || v0.value || '').trim());
    }
    const playerSim = inst.sp ?? (inst.player && inst.player.similarity);
    const playerName = inst.p || (inst.player && (inst.player.name || inst.player.display_name));
    const hasFace = typeof playerSim === 'number';
    const hasName = Boolean(String(playerName || '').trim());
    if (side === 'back') {
        if (hasNumber && hasName) return { ivs: 'BNN', db: 'jersey_back_num_name' };
        if (hasNumber) return { ivs: 'BN', db: 'jersey_back_num_only' };
        if (hasName) return { ivs: 'BNA', db: 'jersey_back_name_only' };
        return { ivs: 'JB', db: 'jersey_back_only' };
    }
    if (hasFace) return { ivs: 'FF', db: 'jersey_front_face' };
    return { ivs: 'JF', db: 'jersey_front_only' };
}

function normalizeDetection(det) {
    const meta = det?.metadata || {};

    // New schema extraction (supports compact nested shape)
    const teamsArray = Array.isArray(meta.teams) ? meta.teams : [];
    const primaryTeam = teamsArray.length ? (teamsArray[0] || {}) : {};
    const teamNew = primaryTeam.t || primaryTeam.team;
    let playerNew;
    let numberNew;
    const instances = Array.isArray(primaryTeam.i) ? primaryTeam.i : (Array.isArray(primaryTeam.instances) ? primaryTeam.instances : []);
    if (instances.length > 0) {
        const inst0 = instances[0] || {};
        playerNew = inst0.p || inst0.player;
        // number can be object { t, c, b } or array/single
        if (inst0.n && typeof inst0.n === 'object' && !Array.isArray(inst0.n)) {
            numberNew = inst0.n.t ?? inst0.n.value;
        } else if (Array.isArray(inst0.n) && inst0.n.length > 0) {
            const first = inst0.n[0];
            numberNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
        }
    } else {
        // Older new-schema variant with fields at team level
        playerNew = primaryTeam.p || primaryTeam.player;
        if (Array.isArray(primaryTeam.n) && primaryTeam.n.length > 0) {
            const first = primaryTeam.n[0];
            numberNew = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
        } else if (typeof primaryTeam.n === 'number' || typeof primaryTeam.n === 'string') {
            numberNew = primaryTeam.n;
        }
    }

    // Legacy schema fallback
    const teamLegacy = meta.team;
    const playerLegacy = meta.player;
    const numberLegacy = meta.number;

    // Timing
    const timRaw = meta?.tim || {};
    // Build a merged timing object that supports both the new schema (e.g. lam_start_ms, shown_ms, srch_*_ms)
    // and the legacy fields (ts, sent, shown, search_start, search_end, search_ms).
    const mergedTim = { ...timRaw };
    // Populate legacy aliases from new fields when only new fields are present
    if (!Number.isFinite(mergedTim.ts) && Number.isFinite(mergedTim.lam_start_ms)) mergedTim.ts = mergedTim.lam_start_ms;
    if (!Number.isFinite(mergedTim.sent) && Number.isFinite(mergedTim.ivs_put_start_ms)) mergedTim.sent = mergedTim.ivs_put_start_ms;
    if (!Number.isFinite(mergedTim.shown) && Number.isFinite(mergedTim.shown_ms)) mergedTim.shown = mergedTim.shown_ms;
    if (!Number.isFinite(mergedTim.search_start) && Number.isFinite(mergedTim.srch_start_ms)) mergedTim.search_start = mergedTim.srch_start_ms;
    if (!Number.isFinite(mergedTim.search_end) && Number.isFinite(mergedTim.srch_end_ms)) mergedTim.search_end = mergedTim.srch_end_ms;
    if (!Number.isFinite(mergedTim.search_ms) && Number.isFinite(mergedTim.srch_ms)) mergedTim.search_ms = mergedTim.srch_ms;
    // Populate new aliases from legacy fields when only legacy fields are present
    if (!Number.isFinite(mergedTim.lam_start_ms) && Number.isFinite(mergedTim.ts)) mergedTim.lam_start_ms = mergedTim.ts;
    if (!Number.isFinite(mergedTim.ivs_put_start_ms) && Number.isFinite(mergedTim.sent)) mergedTim.ivs_put_start_ms = mergedTim.sent;
    if (!Number.isFinite(mergedTim.shown_ms) && Number.isFinite(mergedTim.shown)) mergedTim.shown_ms = mergedTim.shown;
    if (!Number.isFinite(mergedTim.srch_start_ms) && Number.isFinite(mergedTim.search_start)) mergedTim.srch_start_ms = mergedTim.search_start;
    if (!Number.isFinite(mergedTim.srch_end_ms) && Number.isFinite(mergedTim.search_end)) mergedTim.srch_end_ms = mergedTim.search_end;
    if (!Number.isFinite(mergedTim.srch_ms) && Number.isFinite(mergedTim.search_ms)) mergedTim.srch_ms = mergedTim.search_ms;
    // Derive search_ms if missing and we have start/end
    if (!Number.isFinite(mergedTim.search_ms)) {
        const ss = Number.isFinite(mergedTim.search_start) ? mergedTim.search_start : undefined;
        const se = Number.isFinite(mergedTim.search_end) ? mergedTim.search_end : undefined;
        if (typeof ss === 'number' && typeof se === 'number') mergedTim.search_ms = se - ss;
    }

    const ts = Number.isFinite(mergedTim.ts) ? mergedTim.ts : undefined;
    const dur = Number.isFinite(mergedTim.dur) ? mergedTim.dur : undefined;
    const sent = Number.isFinite(mergedTim.sent) ? mergedTim.sent : undefined;
    const shown = Number.isFinite(mergedTim.shown) ? mergedTim.shown : undefined;
    const searchStart = Number.isFinite(mergedTim.search_start) ? mergedTim.search_start : undefined;
    const searchEnd = Number.isFinite(mergedTim.search_end) ? mergedTim.search_end : undefined;
    const searchMs = Number.isFinite(mergedTim.search_ms) ? mergedTim.search_ms : undefined;

    // Collect detection types for this detection across all instances
    const typesSet = new Set();
    try {
        const teams = Array.isArray(meta.teams) ? meta.teams : [];
        teams.forEach((t) => {
            const insts = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
            insts.forEach((inst) => {
                const c = classifyInstanceForType(inst);
                if (c && c.ivs) typesSet.add(c.ivs);
            });
        });
    } catch (_) { }

    return {
        id: det.id,
        streamId: det.stream_id,
        imgUrl: meta?.img?.url,
        team: teamNew ?? teamLegacy,
        player: playerNew ?? playerLegacy,
        number: typeof numberLegacy !== 'undefined' ? numberLegacy : numberNew,
        raw: meta,
        skipped: Boolean(meta?.skipped === true),
        reason: meta?.reason || '',
        types: Array.from(typesSet),
        // Include false detection details from API response
        false_detection_details: det.false_detection_details,
        // Preserve the full timing map for forward-compatibility while exposing commonly used fields.
        tim: { ...mergedTim, ts, dur, sent, shown, search_start: searchStart, search_end: searchEnd, search_ms: searchMs }
    };
}

export default function DetectionsHistory() {
    const [detections, setDetections] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(50);
    const [streamFilter, setStreamFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [labelFilter, setLabelFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
    const cleanedBase = rawBase.replace(/^@+/, '');
    const API_BASE = cleanedBase || '/dev';

    const fetchDetections = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            params.set('offset', String(offset));
            params.set('limit', String(limit));
            if (streamFilter) params.set('stream_id_contains', streamFilter);
            // status filter: all | detected | skipped
            if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
            // label filter
            if (labelFilter) params.set('label', labelFilter);
            const res = await fetch(`${API_BASE}/detections?${params.toString()}`, { headers: { accept: 'application/json' } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const items = Array.isArray(data?.items) ? data.items : [];
            // Sort by stream_id ascending as requested
            items.sort((a, b) => String(a.stream_id || '').localeCompare(String(b.stream_id || '')));
            setDetections(items.map(normalizeDetection));
            setTotal(Number.isFinite(data?.total) ? data.total : items.length);
        } catch (e) {
            setError('Failed to load detections');
        } finally {
            setLoading(false);
        }
    }, [API_BASE, offset, limit, streamFilter, statusFilter, labelFilter]);

    useEffect(() => {
        fetchDetections();
    }, [fetchDetections]);

    const canPrev = offset > 0;
    const canNext = offset + limit < total;

    const grouped = useMemo(() => {
        // Group by stream_id for easier scanning
        const map = new Map();
        detections.forEach((d) => {
            const key = d.streamId || 'unknown';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(d);
        });
        return Array.from(map.entries());
    }, [detections]);

    // Analyze per stream
    const [analyzing, setAnalyzing] = useState({}); // sid -> boolean
    const [analysis, setAnalysis] = useState({}); // sid -> data
    const [frameFilter, setFrameFilter] = useState({}); // sid -> 'all' | 'detected' | 'skipped'
    const [typeFilter, setTypeFilter] = useState({}); // sid -> ivs code or ''
    const [reasonFilter, setReasonFilter] = useState({}); // sid -> reason string or ''
    const [analysisCollapsed, setAnalysisCollapsed] = useState({}); // sid -> boolean
    const [compareSelection, setCompareSelection] = useState([]); // [sid1, sid2]
    const [compareOpen, setCompareOpen] = useState(false);
    const [compareEntryOpen, setCompareEntryOpen] = useState(false);
    const [compareInputA, setCompareInputA] = useState('');
    const [compareInputB, setCompareInputB] = useState('');
    const fetchAnalysis = useCallback(async (sid) => {
        if (!sid) return;
        setAnalyzing((s) => ({ ...s, [sid]: true }));
        try {
            const res = await fetch(`${API_BASE}/detections/analyze?stream_id=${encodeURIComponent(sid)}`, { headers: { accept: 'application/json' } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setAnalysis((m) => ({ ...m, [sid]: data }));
        } catch (e) {
            setAnalysis((m) => ({ ...m, [sid]: { error: e?.message || 'Failed to analyze' } }));
        } finally {
            setAnalyzing((s) => ({ ...s, [sid]: false }));
        }
    }, [API_BASE]);

    const AnalysisPanel = ({ sid, data, activeType, onTypeClick, activeReason, onReasonClick }) => {
        if (!data || data.error) {
            return <div className="mt-3 text-sm text-red-300">{data?.error || 'No analysis available'}</div>;
        }
        const total = data.total_frames || 0;
        const detected = data.detected_frames || 0;
        const skipped = data.skipped_frames || 0;
        const reasons = Array.isArray(data.skip_reasons) ? data.skip_reasons : [];
        const types = Array.isArray(data.instance_types) ? data.instance_types : [];
        const timeline = Array.isArray(data.timeline) ? data.timeline : [];
        const detectedLabels = Array.isArray(data.detected_labels) ? data.detected_labels : [];
        const falseLabels = Array.isArray(data.false_labels) ? data.false_labels : [];
        // Build legends for types (detected + skipped) and reasons present in the timeline
        const typeKeys = new Set();
        const reasonKeys = new Set();
        timeline.forEach(pt => {
            const dbt = pt.detected_by_type || {};
            Object.keys(dbt).forEach(k => typeKeys.add(k));
            const sbt = pt.skipped_by_type || {};
            Object.keys(sbt).forEach(k => typeKeys.add(k));
            const sbr = pt.skipped_by_reason || {};
            Object.keys(sbr).forEach(k => reasonKeys.add(k));
        });
        const typePalette = {
            FF: '#3b82f6', // blue
            BNN: '#10b981', // emerald
            BN: '#06b6d4', // cyan
            BNA: '#8b5cf6', // violet
            JF: '#9ca3af',
            JB: '#6b7280',
            UNK: '#a3e635', // lime as fallback
        };
        const reasonPalette = ['#f59e0b', '#f97316', '#ef4444', '#eab308', '#84cc16', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1', '#8b5cf6'];
        const colorForReason = (r) => {
            // stable index based on hash
            let h = 0;
            const s = String(r);
            for (let i = 0; i < s.length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
            const idx = Math.abs(h) % reasonPalette.length;
            return reasonPalette[idx];
        };
        return (
            <div className="mt-3 p-3 rounded-lg bg-gradient-to-br from-[#0a1628] to-[#1a2448] border border-[#4cc3e9]/30">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-white/70">Total Frames</div>
                        <div className="text-xl font-bold">{total}</div>
                    </div>
                    <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-3">
                        <div className="text-emerald-300">Detected Frames</div>
                        <div className="text-xl font-bold text-white">{detected}</div>
                    </div>
                    <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 p-3">
                        <div className="text-amber-300">Skipped Frames</div>
                        <div className="text-xl font-bold text-white">{skipped}</div>
                    </div>
                </div>

                <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-sm font-semibold text-white/80 mb-2">Skipped Reasons</div>
                        {reasons.length === 0 ? (
                            <div className="text-white/60 text-sm">—</div>
                        ) : (
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-white/60"><th className="text-left py-1">Reason</th><th className="text-right py-1">Count</th></tr>
                                </thead>
                                <tbody>
                                    {reasons.map((r, i) => {
                                        const isActive = String(activeReason || '') === String(r.reason || '');
                                        return (
                                            <tr
                                                key={i}
                                                className={`border-t border-white/10 ${isActive ? 'bg-amber-500/10' : 'hover:bg-white/5'}`}
                                            >
                                                <td className="py-1 pr-2 text-white/90">
                                                    <button
                                                        type="button"
                                                        className={`inline-flex items-center gap-2 hover:underline ${isActive ? 'text-amber-300' : ''}`}
                                                        title="Filter frames by this reason"
                                                        onClick={() => onReasonClick && onReasonClick(sid, String(r.reason || ''))}
                                                    >
                                                        <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: colorForReason(String(r.reason || '')) }} />
                                                        {r.reason}
                                                    </button>
                                                </td>
                                                <td className="py-1 pl-2 text-right text-white/90">{r.count}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                        {activeReason ? (
                            <div className="mt-2 text-[10px] text-white/70 flex items-center gap-2">
                                <span>Active reason:</span>
                                <span className="px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-400/30 text-amber-200">{String(activeReason)}</span>
                                <button
                                    type="button"
                                    className="px-1.5 py-0.5 rounded border border-white/20 bg-white/10 hover:bg-white/20 text-white"
                                    onClick={() => onReasonClick && onReasonClick(sid, '')}
                                >
                                    Clear
                                </button>
                            </div>
                        ) : (
                            <div className="mt-2 text-[10px] text-white/60">Tip: click a reason to filter frames</div>
                        )}
                    </div>
                    {/* Detected Types (aggregate) */}
                    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-sm font-semibold text-white/80 mb-2">Detected Types</div>
                        {(() => {
                            const detTotals = {};
                            timeline.forEach(pt => { const dt = pt.detected_by_type || {}; Object.keys(dt).forEach(k => detTotals[k] = (detTotals[k] || 0) + (dt[k] || 0)); });
                            const rows = Object.entries(detTotals).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                            if (rows.length === 0) return <div className="text-white/60 text-sm">—</div>;
                            const ivsName = { JF: 'jersey_front_only', JB: 'jersey_back_only', FF: 'jersey_front_face', BNN: 'jersey_back_num_name', BN: 'jersey_back_num_only', BNA: 'jersey_back_name_only', UNK: 'unknown' };
                            return (
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-white/60">
                                            <th className="text-left py-1">Code</th>
                                            <th className="text-left py-1">Name</th>
                                            <th className="text-right py-1">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(([k, v]) => (
                                            <tr key={k} className="border-t border-white/10">
                                                <td className="py-1 pr-2 text-white/90"><span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ backgroundColor: typePalette[k] || '#64748b' }} />{k}</td>
                                                <td className="py-1 pr-2 text-white/70">{ivsName[k] || 'unknown'}</td>
                                                <td className="py-1 pl-2 text-right text-white/90">{v}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>
                    {/* Skipped Types (aggregate) */}
                    <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-sm font-semibold text-white/80 mb-2">Skipped Types</div>
                        {(() => {
                            const skpTotals = {};
                            timeline.forEach(pt => { const st = pt.skipped_by_type || {}; Object.keys(st).forEach(k => skpTotals[k] = (skpTotals[k] || 0) + (st[k] || 0)); });
                            const rows = Object.entries(skpTotals).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                            if (rows.length === 0) return <div className="text-white/60 text-sm">—</div>;
                            const ivsName = { JF: 'jersey_front_only', JB: 'jersey_back_only', FF: 'jersey_front_face', BNN: 'jersey_back_num_name', BN: 'jersey_back_num_only', BNA: 'jersey_back_name_only', UNK: 'unknown' };
                            return (
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-white/60">
                                            <th className="text-left py-1">Code</th>
                                            <th className="text-left py-1">Name</th>
                                            <th className="text-right py-1">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map(([k, v]) => (
                                            <tr key={k} className="border-t border-white/10">
                                                <td className="py-1 pr-2 text-white/90"><span className="inline-block w-2 h-2 rounded-sm mr-2" style={{ backgroundColor: typePalette[k] || '#64748b' }} />{k}</td>
                                                <td className="py-1 pr-2 text-white/70">{ivsName[k] || 'unknown'}</td>
                                                <td className="py-1 pl-2 text-right text-white/90">{v}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            );
                        })()}
                    </div>
                </div>

                {/* Label Statistics */}
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Detected Labels */}
                    <div className="rounded-lg bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-400/30 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold text-green-300">Detected Labels</div>
                            <div className="text-xs text-green-300 bg-green-500/20 px-2 py-1 rounded">
                                Total: {detectedLabels.reduce((sum, label) => sum + label.count, 0)}
                            </div>
                        </div>
                        {detectedLabels.length === 0 ? (
                            <div className="text-white/60 text-sm">No labels detected</div>
                        ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                {detectedLabels.map((label, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between p-2 rounded bg-green-500/10 border border-green-400/20 hover:bg-green-500/20 cursor-pointer transition-colors"
                                        onClick={() => {
                                            setLabelFilter(label.label);
                                            setOffset(0); // Reset to first page
                                        }}
                                        title={`Click to filter detections with "${label.label}"`}
                                    >
                                        <span className="text-sm text-white truncate">{label.label}</span>
                                        <span className="text-xs text-green-300 bg-green-500/20 px-2 py-1 rounded ml-2">{label.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {detectedLabels.length > 0 && (
                            <div className="mt-2 text-xs text-white/60">Click a label to filter detections</div>
                        )}
                    </div>

                    {/* False Labels */}
                    <div className="rounded-lg bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-400/30 p-3">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold text-red-300">False Detection Labels</div>
                            <div className="text-xs text-red-300 bg-red-500/20 px-2 py-1 rounded">
                                Total: {falseLabels.reduce((sum, label) => sum + label.count, 0)}
                            </div>
                        </div>
                        {falseLabels.length === 0 ? (
                            <div className="text-white/60 text-sm">No false labels reported</div>
                        ) : (
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                                {falseLabels.map((label, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center justify-between p-2 rounded bg-red-500/10 border border-red-400/20 hover:bg-red-500/20 cursor-pointer transition-colors"
                                        onClick={() => {
                                            setLabelFilter(label.label);
                                            setOffset(0); // Reset to first page
                                        }}
                                        title={`Click to filter detections with false label "${label.label}"`}
                                    >
                                        <span className="text-sm text-white truncate">❌ {label.label}</span>
                                        <span className="text-xs text-red-300 bg-red-500/20 px-2 py-1 rounded ml-2">{label.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        {falseLabels.length > 0 && (
                            <div className="mt-2 text-xs text-white/60">Click a false label to see problematic detections</div>
                        )}
                    </div>
                </div>

                {/* Totals by detection type (full stream) */}
                <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-3">
                    <div className="text-sm font-semibold text-white/80 mb-2">Totals by detection type (full stream)</div>
                    {(() => {
                        // aggregate totals from timeline
                        const detTotals = {};
                        const skpTotals = {};
                        timeline.forEach(pt => {
                            const dt = pt.detected_by_type || {};
                            Object.keys(dt).forEach(k => { detTotals[k] = (detTotals[k] || 0) + (dt[k] || 0); });
                            const st = pt.skipped_by_type || {};
                            Object.keys(st).forEach(k => { skpTotals[k] = (skpTotals[k] || 0) + (st[k] || 0); });
                        });
                        const detSum = Object.values(detTotals).reduce((a, b) => a + (b || 0), 0);
                        const skpSum = Object.values(skpTotals).reduce((a, b) => a + (b || 0), 0);
                        const detKeys = Object.keys(detTotals).sort();
                        const skpKeys = Object.keys(skpTotals).sort();
                        return (
                            <div className="space-y-2">
                                <div className="text-xs text-white/70">Detected</div>
                                <div className="h-4 bg-white/10 rounded overflow-hidden flex">
                                    {detKeys.map((k) => {
                                        const val = detTotals[k] || 0;
                                        const w = detSum > 0 ? (val / detSum) * 100 : 0;
                                        if (w <= 0) return null;
                                        return <div key={`agg-det-${k}`} className="h-full" title={`${k}: ${val}`} style={{ width: `${w}%`, backgroundColor: typePalette[k] || '#64748b' }} />
                                    })}
                                </div>
                                <div className="text-xs text-white/70 mt-2">Skipped</div>
                                <div className="h-4 bg-white/10 rounded overflow-hidden flex">
                                    {skpKeys.map((k) => {
                                        const val = skpTotals[k] || 0;
                                        const w = skpSum > 0 ? (val / skpSum) * 100 : 0;
                                        if (w <= 0) return null;
                                        return <div key={`agg-skp-${k}`} className="h-full" title={`${k}: ${val}`} style={{ width: `${w}%`, backgroundColor: typePalette[k] || '#64748b' }} />
                                    })}
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Single bar chart with types on X-axis, counts on Y-axis (detected vs skipped grouped) */}
                <div className="mt-3 rounded-lg bg-white/5 border border-white/10 p-3">
                    <div className="text-sm font-semibold text-white/80 mb-2">Counts by type</div>
                    {(() => {
                        // Aggregate totals from timeline
                        const detTotals = {};
                        const skpTotals = {};
                        timeline.forEach(pt => {
                            const dt = pt.detected_by_type || {};
                            Object.keys(dt).forEach(k => { detTotals[k] = (detTotals[k] || 0) + (dt[k] || 0); });
                            const st = pt.skipped_by_type || {};
                            Object.keys(st).forEach(k => { skpTotals[k] = (skpTotals[k] || 0) + (st[k] || 0); });
                        });
                        const allKeysSet = new Set([...Object.keys(detTotals), ...Object.keys(skpTotals)]);
                        const allKeys = Array.from(allKeysSet).sort((a, b) => {
                            const av = (detTotals[a] || 0) + (skpTotals[a] || 0);
                            const bv = (detTotals[b] || 0) + (skpTotals[b] || 0);
                            return (bv - av) || a.localeCompare(b);
                        });
                        if (allKeys.length === 0) return <div className="text-white/60 text-sm">—</div>;
                        const ivsName = { JF: 'jersey_front_only', JB: 'jersey_back_only', FF: 'jersey_front_face', BNN: 'jersey_back_num_name', BN: 'jersey_back_num_only', BNA: 'jersey_back_name_only', UNK: 'unknown' };
                        const data = allKeys.map((k) => ({ type: k, detected: detTotals[k] || 0, skipped: skpTotals[k] || 0, name: ivsName[k] || '' }));
                        const handleBarClick = (entry) => {
                            if (entry && entry.activeLabel) {
                                const code = String(entry.activeLabel);
                                onTypeClick && onTypeClick(sid, code);
                            }
                        };
                        return (
                            <div style={{ width: '100%', height: 280 }}>
                                <ResponsiveContainer>
                                    <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }} onClick={handleBarClick}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                        <XAxis dataKey="type" tick={{ fill: '#cbd5e1', fontSize: 10 }} angle={-25} textAnchor="end" interval={0} height={50} />
                                        <YAxis tick={{ fill: '#cbd5e1', fontSize: 10 }} allowDecimals={false} />
                                        <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }} labelFormatter={(lbl) => `${lbl} (${ivsName[lbl] || 'unknown'})`} />
                                        <Legend wrapperStyle={{ color: '#e2e8f0' }} />
                                        <Bar dataKey="detected" name="Detected" fill="#22c55e" radius={[4, 4, 0, 0]} />
                                        <Bar dataKey="skipped" name="Skipped" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-[10px] text-white/80">
                                    {allKeys.map((k) => (
                                        <div key={`meaning-${k}`} className="flex items-center gap-2">
                                            <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: typePalette[k] || '#64748b' }} />
                                            <button type="button" className="hover:underline" onClick={() => onTypeClick && onTypeClick(sid, k)}>{k}</button>
                                            <span className="text-white/60">— {ivsName[k] || 'unknown'}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-1 text-[10px] text-white/60">Tip: click a bar or code to filter images by that type</div>
                            </div>
                        );
                    })()}
                </div>
            </div>
        );
    };

    const CompareModal = ({ a, b, onClose }) => {
        const da = analysis[a] || {};
        const db = analysis[b] || {};
        const aTotals = {
            total: da.total_frames || 0,
            detected: da.detected_frames || 0,
            skipped: da.skipped_frames || 0,
        };
        const bTotals = {
            total: db.total_frames || 0,
            detected: db.detected_frames || 0,
            skipped: db.skipped_frames || 0,
        };
        const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(1)}%` : '—');

        const makeTypeTotals = (tl) => {
            const detTotals = {}; const skpTotals = {};
            (Array.isArray(tl) ? tl : []).forEach(pt => {
                const dt = pt.detected_by_type || {}; Object.keys(dt).forEach(k => detTotals[k] = (detTotals[k] || 0) + (dt[k] || 0));
                const st = pt.skipped_by_type || {}; Object.keys(st).forEach(k => skpTotals[k] = (skpTotals[k] || 0) + (st[k] || 0));
            });
            return { detTotals, skpTotals };
        };
        const aTypes = makeTypeTotals(da.timeline);
        const bTypes = makeTypeTotals(db.timeline);
        const ivsName = { JF: 'jersey_front_only', JB: 'jersey_back_only', FF: 'jersey_front_face', BNN: 'jersey_back_num_name', BN: 'jersey_back_num_only', BNA: 'jersey_back_name_only', UNK: 'unknown' };
        const detKeys = Array.from(new Set([
            ...Object.keys(aTypes.detTotals || {}),
            ...Object.keys(bTypes.detTotals || {}),
        ]));
        const aDetSum = Object.values(aTypes.detTotals || {}).reduce((acc, v) => acc + (v || 0), 0);
        const bDetSum = Object.values(bTypes.detTotals || {}).reduce((acc, v) => acc + (v || 0), 0);
        detKeys.sort((k1, k2) => {
            const s1 = (aTypes.detTotals?.[k1] || 0) + (bTypes.detTotals?.[k1] || 0);
            const s2 = (aTypes.detTotals?.[k2] || 0) + (bTypes.detTotals?.[k2] || 0);
            return (s2 - s1) || k1.localeCompare(k2);
        });

        return (
            <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
                <div className="relative w-full max-w-5xl bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-[#4cc3e9]">Compare Streams</div>
                        <button type="button" onClick={onClose} className="px-3 py-1.5 rounded text-xs bg-white/10 border border-white/20 hover:bg-white/20">Close</button>
                    </div>
                    <div className="text-xs text-white/70 mb-3">{a} vs {b}</div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-white/60">
                                    <th className="text-left py-2 pr-2">Metric</th>
                                    <th className="text-right py-2 pr-2">{a}</th>
                                    <th className="text-right py-2 pl-2">{b}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-t border-white/10"><td className="py-2 pr-2">Total Frames</td><td className="py-2 pr-2 text-right">{aTotals.total}</td><td className="py-2 pl-2 text-right">{bTotals.total}</td></tr>
                                <tr className="border-t border-white/10"><td className="py-2 pr-2">Detected Frames</td><td className="py-2 pr-2 text-right">{aTotals.detected} <span className="text-white/50">({pct(aTotals.detected, aTotals.total)})</span></td><td className="py-2 pl-2 text-right">{bTotals.detected} <span className="text-white/50">({pct(bTotals.detected, bTotals.total)})</span></td></tr>
                                <tr className="border-t border-white/10"><td className="py-2 pr-2">Skipped Frames</td><td className="py-2 pr-2 text-right">{aTotals.skipped} <span className="text-white/50">({pct(aTotals.skipped, aTotals.total)})</span></td><td className="py-2 pl-2 text-right">{bTotals.skipped} <span className="text-white/50">({pct(bTotals.skipped, bTotals.total)})</span></td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Optional: top skipped reasons side-by-side */}
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                            <div className="text-sm font-semibold text-white/80 mb-2">Top Skipped Reasons ({a})</div>
                            {Array.isArray(da.skip_reasons) && da.skip_reasons.length > 0 ? (
                                <table className="w-full text-xs"><tbody>{da.skip_reasons.slice().sort((x, y) => (y.count || 0) - (x.count || 0)).slice(0, 10).map((r, i) => (<tr key={`a-${i}`} className="border-t border-white/10"><td className="py-1 pr-2">{r.reason}</td><td className="py-1 pl-2 text-right">{r.count}</td></tr>))}</tbody></table>
                            ) : (<div className="text-white/60 text-sm">—</div>)}
                        </div>
                        <div className="rounded-lg bg-white/5 border border-white/10 p-3">
                            <div className="text-sm font-semibold text-white/80 mb-2">Top Skipped Reasons ({b})</div>
                            {Array.isArray(db.skip_reasons) && db.skip_reasons.length > 0 ? (
                                <table className="w-full text-xs"><tbody>{db.skip_reasons.slice().sort((x, y) => (y.count || 0) - (x.count || 0)).slice(0, 10).map((r, i) => (<tr key={`b-${i}`} className="border-t border-white/10"><td className="py-1 pr-2">{r.reason}</td><td className="py-1 pl-2 text-right">{r.count}</td></tr>))}</tbody></table>
                            ) : (<div className="text-white/60 text-sm">—</div>)}
                        </div>
                    </div>

                    {/* Detected totals by type: two tables, one per stream */}
                    <div className="mt-4 rounded-lg bg-white/5 border border-white/10 p-3">
                        <div className="text-sm font-semibold text-white/80 mb-2">Detected totals by type</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-white/60 mb-2">{a}</div>
                                {Object.keys(aTypes.detTotals || {}).length === 0 ? (
                                    <div className="text-white/60 text-sm">—</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-white/60">
                                                    <th className="text-left py-1 pr-2">Code</th>
                                                    <th className="text-left py-1 pr-2">Name</th>
                                                    <th className="text-right py-1 pr-2">Count</th>
                                                    <th className="text-right py-1 pr-2">%</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(aTypes.detTotals || {})
                                                    .sort(([, v1], [, v2]) => (v2 || 0) - (v1 || 0))
                                                    .map(([k, v]) => {
                                                        const pct = aDetSum > 0 ? (((v || 0) / aDetSum) * 100).toFixed(1) + '%' : '—';
                                                        return (
                                                            <tr key={`a-det-${k}`} className="border-t border-white/10">
                                                                <td className="py-1 pr-2 text-white/90">{k}</td>
                                                                <td className="py-1 pr-2 text-white/70">{ivsName[k] || 'unknown'}</td>
                                                                <td className="py-1 pr-2 text-right text-white/90">{v || 0}</td>
                                                                <td className="py-1 pr-2 text-right text-white/60">{pct}</td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div>
                                <div className="text-xs text-white/60 mb-2">{b}</div>
                                {Object.keys(bTypes.detTotals || {}).length === 0 ? (
                                    <div className="text-white/60 text-sm">—</div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-white/60">
                                                    <th className="text-left py-1 pr-2">Code</th>
                                                    <th className="text-left py-1 pr-2">Name</th>
                                                    <th className="text-right py-1 pr-2">Count</th>
                                                    <th className="text-right py-1 pr-2">%</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(bTypes.detTotals || {})
                                                    .sort(([, v1], [, v2]) => (v2 || 0) - (v1 || 0))
                                                    .map(([k, v]) => {
                                                        const pct = bDetSum > 0 ? (((v || 0) / bDetSum) * 100).toFixed(1) + '%' : '—';
                                                        return (
                                                            <tr key={`b-det-${k}`} className="border-t border-white/10">
                                                                <td className="py-1 pr-2 text-white/90">{k}</td>
                                                                <td className="py-1 pr-2 text-white/70">{ivsName[k] || 'unknown'}</td>
                                                                <td className="py-1 pr-2 text-right text-white/90">{v || 0}</td>
                                                                <td className="py-1 pr-2 text-right text-white/60">{pct}</td>
                                                            </tr>
                                                        );
                                                    })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="mt-2 text-[10px] text-white/60">Percentages are within each stream’s detected total.</div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex flex-col text-white pt-14 sm:pt-16">
            <Navbar />
            <main className="flex-1">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="mb-4 grid grid-cols-1 md:grid-cols-6 gap-3">
                        <input
                            value={streamFilter}
                            onChange={(e) => setStreamFilter(e.target.value)}
                            placeholder="Filter by stream_id"
                            className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 text-sm md:col-span-2 placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                        />
                        <input
                            value={labelFilter}
                            onChange={(e) => setLabelFilter(e.target.value)}
                            placeholder="Filter by label"
                            className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 text-sm placeholder-white/50 focus:border-[#4cc3e9]/50 focus:outline-none transition-colors"
                        />
                        <select value={statusFilter} onChange={(e) => { setOffset(0); setStatusFilter(e.target.value); }} className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 text-sm focus:border-[#4cc3e9]/50 focus:outline-none transition-colors">
                            <option value="all" className="bg-[#1a2448]">All statuses</option>
                            <option value="detected" className="bg-[#1a2448]">Detected</option>
                            <option value="skipped" className="bg-[#1a2448]">Skipped</option>
                        </select>
                        <select value={limit} onChange={(e) => { setOffset(0); setLimit(Number(e.target.value)); }} className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 text-sm focus:border-[#4cc3e9]/50 focus:outline-none transition-colors">
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n} className="bg-[#1a2448]">{n} per page</option>
                            ))}
                        </select>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => { setOffset(0); fetchDetections(); }} className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-sm font-medium transition-all transform hover:scale-105">
                                Apply
                            </button>
                            <button type="button" onClick={() => { setStreamFilter(''); setLabelFilter(''); setStatusFilter('all'); setOffset(0); }} className="px-3 py-2 rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 text-sm transition-colors">
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={() => setCompareEntryOpen(true)}
                                className="px-3 py-2 rounded-lg text-sm font-medium transition-colors bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/40 hover:border-[#4cc3e9]/60"
                                title="Compare two streams by ID"
                            >
                                Compare streams
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mb-6 text-sm text-[#4cc3e9]/80">
                        <div className="font-medium flex items-center gap-3">
                            <span>Total: <span className="text-white">{total}</span></span>
                            {labelFilter && (
                                <span className="px-2 py-1 rounded bg-[#4cc3e9]/20 border border-[#4cc3e9]/40 text-[#4cc3e9] text-xs flex items-center gap-2">
                                    Label: {labelFilter}
                                    <button
                                        type="button"
                                        onClick={() => { setLabelFilter(''); setOffset(0); }}
                                        className="text-[#4cc3e9] hover:text-white"
                                        title="Clear label filter"
                                    >
                                        ×
                                    </button>
                                </span>
                            )}
                        </div>
                        <div className="text-white/60">Offset: {offset} • Limit: {limit}</div>
                    </div>

                    {loading && (
                        <div className="text-[#4cc3e9] text-sm font-medium flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-[#4cc3e9]/30 border-t-[#4cc3e9] rounded-full animate-spin"></div>
                            Loading…
                        </div>
                    )}
                    {error && (
                        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>
                    )}

                    {!loading && !error && grouped.length === 0 && (
                        <div className="text-[#4cc3e9]/60 text-sm text-center py-12">No detections found.</div>
                    )}

                    {!loading && !error && grouped.length > 0 && (
                        <div className="space-y-6">
                            {grouped.map(([sid, items]) => (
                                <div key={sid} className="">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <div className="text-xs uppercase tracking-wide text-[#4cc3e9] font-semibold bg-gradient-to-r from-[#1a2448]/60 to-transparent p-2 rounded-lg border-l-2 border-[#4cc3e9]">Stream: {sid}</div>
                                            <AddToDataset
                                                streamId={sid}
                                                mode="button"
                                                compact={true}
                                                className="ml-2"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {/* Type filter chips for this stream */}
                                            {(() => {
                                                const typeSet = new Set();
                                                items.forEach((it) => Array.isArray(it.types) && it.types.forEach((t) => typeSet.add(t)));
                                                const codes = Array.from(typeSet).sort();
                                                if (codes.length === 0) return null;
                                                return (
                                                    <div className="hidden md:flex items-center gap-1 mr-2">
                                                        {codes.map((code) => (
                                                            <button key={code} type="button" onClick={() => setTypeFilter((m) => ({ ...m, [sid]: (m[sid] === code ? '' : code) }))} className={`px-2 py-0.5 rounded text-[10px] ${typeFilter[sid] === code ? 'bg-white text-black' : 'bg-white/10 text-white'} border border-white/20 hover:bg-white/20`}>{code}</button>
                                                        ))}
                                                        {typeFilter[sid] ? (
                                                            <button type="button" onClick={() => setTypeFilter((m) => ({ ...m, [sid]: '' }))} className="px-2 py-0.5 rounded text-[10px] bg-white/10 text-white border border-white/20 hover:bg-white/20">Clear</button>
                                                        ) : null}
                                                    </div>
                                                );
                                            })()}
                                            {/* Frame status filter: All / Detected / Skipped */}
                                            <div className="hidden md:flex items-center gap-1 mr-2">
                                                {['all', 'detected', 'skipped'].map((opt) => (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        onClick={() => {
                                                            setFrameFilter((m) => ({ ...m, [sid]: opt }));
                                                            if (opt === 'detected') {
                                                                // clear reason filter when switching to detected
                                                                setReasonFilter((m) => ({ ...m, [sid]: '' }));
                                                            }
                                                        }}
                                                        className={`px-2 py-0.5 rounded text-[10px] border ${(frameFilter[sid] || 'all') === opt ? 'bg-[#4cc3e9] text-black border-[#4cc3e9]' : 'bg-white/10 text-white border-white/20 hover:bg-white/20'}`}
                                                        title={`Show ${opt}`}
                                                    >{opt.charAt(0).toUpperCase() + opt.slice(1)}</button>
                                                ))}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => fetchAnalysis(sid)}
                                                disabled={!!analyzing[sid]}
                                                className={`px-3 py-1.5 rounded text-xs font-medium border ${analyzing[sid] ? 'bg-white/5 border-white/10 text-white/50 cursor-not-allowed' : 'bg-white/10 border-white/20 hover:bg-white/20 hover:border-white/40'}`}
                                            >
                                                {analyzing[sid] ? 'Analyzing…' : 'Analyze'}
                                            </button>
                                            {analysis[sid] ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setAnalysisCollapsed((m) => ({ ...m, [sid]: !m[sid] }))}
                                                    className={`px-3 py-1.5 rounded text-xs font-medium border bg-white/10 border-white/20 hover:bg-white/20 hover:border-white/40`}
                                                >
                                                    {analysisCollapsed[sid] ? 'Show analysis' : 'Hide analysis'}
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>
                                    {analysis[sid] && !analysisCollapsed[sid] && (
                                        <AnalysisPanel
                                            sid={sid}
                                            data={analysis[sid]}
                                            activeType={typeFilter[sid] || ''}
                                            onTypeClick={(sid, code) => setTypeFilter((m) => ({ ...m, [sid]: (m[sid] === code ? '' : code) }))}
                                            activeReason={reasonFilter[sid] || ''}
                                            onReasonClick={(sid, reason) => {
                                                setReasonFilter((m) => ({ ...m, [sid]: reason }));
                                                // auto-switch to skipped when a reason is selected
                                                setFrameFilter((m) => ({ ...m, [sid]: reason ? 'skipped' : (m[sid] || 'all') }));
                                            }}
                                        />
                                    )}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                        {items.filter((d) => {
                                            // type filter
                                            const tf = typeFilter[sid] || '';
                                            if (tf && !(Array.isArray(d.types) && d.types.includes(tf))) return false;
                                            // frame status filter
                                            const sf = (frameFilter[sid] || 'all');
                                            if (sf === 'detected' && d.skipped) return false;
                                            if (sf === 'skipped' && !d.skipped) return false;
                                            // reason filter (applies to skipped frames only)
                                            const rf = reasonFilter[sid] || '';
                                            if (rf) {
                                                if (!d.skipped) return false;
                                                if (String(d.reason || '') !== String(rf)) return false;
                                            }
                                            return true;
                                        }).map((d, index, filteredDetections) => (
                                            <DetectionCard 
                                                key={d.id} 
                                                det={d} 
                                                allDetections={filteredDetections}
                                                currentIndex={index}
                                                onNavigate={(newIndex) => {
                                                    // Navigation handled within the component itself
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {compareOpen && compareSelection.length === 2 && (
                        <CompareModal
                            a={compareSelection[0]}
                            b={compareSelection[1]}
                            onClose={() => setCompareOpen(false)}
                        />
                    )}

                    {compareEntryOpen && (
                        <div className="fixed inset-0 z-[9998] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setCompareEntryOpen(false)}>
                            <div className="relative w-full max-w-md bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 p-4" onClick={(e) => e.stopPropagation()}>
                                <div className="text-sm font-semibold text-[#4cc3e9] mb-3">Compare streams</div>
                                <div className="space-y-2">
                                    <input value={compareInputA} onChange={(e) => setCompareInputA(e.target.value)} placeholder="First stream_id" className="w-full px-3 py-2 rounded bg-white/10 border border-white/20 placeholder-white/50" />
                                    <input value={compareInputB} onChange={(e) => setCompareInputB(e.target.value)} placeholder="Second stream_id" className="w-full px-3 py-2 rounded bg-white/10 border border-white/20 placeholder-white/50" />
                                </div>
                                <div className="mt-4 flex items-center justify-end gap-2">
                                    <button type="button" onClick={() => setCompareEntryOpen(false)} className="px-3 py-1.5 rounded text-xs bg-white/10 border border-white/20 hover:bg-white/20">Cancel</button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const a = (compareInputA || '').trim();
                                            const b = (compareInputB || '').trim();
                                            if (!a || !b) return;
                                            if (!analysis[a]) await fetchAnalysis(a);
                                            if (!analysis[b]) await fetchAnalysis(b);
                                            setCompareSelection([a, b]);
                                            setCompareEntryOpen(false);
                                            setCompareOpen(true);
                                        }}
                                        className="px-3 py-1.5 rounded text-xs bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/40 hover:border-[#4cc3e9]/60"
                                    >
                                        Compare
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex items-center justify-between">
                        <button
                            type="button"
                            disabled={!canPrev}
                            onClick={() => { const next = Math.max(0, offset - limit); setOffset(next); }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${canPrev ? 'bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 hover:scale-105' : 'bg-[#1a2448]/30 border border-white/5 opacity-50 cursor-not-allowed'}`}
                        >
                            Previous
                        </button>
                        <button
                            type="button"
                            disabled={!canNext}
                            onClick={() => { const next = offset + limit; setOffset(next); }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${canNext ? 'bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 hover:scale-105' : 'bg-[#1a2448]/30 border border-white/5 opacity-50 cursor-not-allowed'}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Timing({ det, isModal = false }) {
    const t = det?.tim || {};
    // Prefer new fields; fallback to legacy
    const ts = Number.isFinite(t.lam_start_ms) ? t.lam_start_ms : t.ts;
    const lamDone = Number.isFinite(t.lam_build_done_ms) ? t.lam_build_done_ms : undefined;
    const sent = Number.isFinite(t.ivs_put_start_ms) ? t.ivs_put_start_ms : t.sent;
    const recv = Number.isFinite(t.clt_recv_ms) ? t.clt_recv_ms : undefined;
    const srchStart = Number.isFinite(t.srch_start_ms) ? t.srch_start_ms : (Number.isFinite(t.search_start) ? t.search_start : undefined);
    const srchEnd = Number.isFinite(t.srch_end_ms) ? t.srch_end_ms : (Number.isFinite(t.search_end) ? t.search_end : undefined);
    const shown = Number.isFinite(t.shown_ms) ? t.shown_ms : t.shown;

    if (!ts && !sent && !shown) return null;

    const lamExec = Number.isFinite(t.dur) ? t.dur : (Number.isFinite(lamDone) && Number.isFinite(ts) ? (lamDone - ts) : undefined);
    const dur = lamExec; // back-compat alias to avoid runtime errors in older UI bits
    const svToCl = (Number.isFinite(recv) && Number.isFinite(sent)) ? (recv - sent) : undefined; // Sent -> Arrived
    const srchLatency = (Number.isFinite(srchEnd) && Number.isFinite(srchStart)) ? (srchEnd - srchStart) : undefined;
    const clToShown = (Number.isFinite(shown) && Number.isFinite(recv)) ? (shown - recv) : undefined; // Arrived -> Shown
    const totalDuration = (Number.isFinite(shown) && Number.isFinite(ts)) ? (shown - ts) : undefined;
    // Back-compat durations used by some UI rows
    const metadataSentLatency = (Number.isFinite(sent) && Number.isFinite(ts)) ? (sent - ts) : undefined;
    const deliveryLatency = (Number.isFinite(shown) && Number.isFinite(sent)) ? (shown - sent) : undefined;

    const fmtMs = (v) => (typeof v === 'number' ? `${v} ms` : '–');
    const fmtMsAndSec = (v) => (typeof v === 'number' ? `${v} ms (${(v / 1000).toFixed(2)}s)` : '–');
    // Show only time-of-day (HH:MM:SS.mmm), no dates for clarity
    const fmtTime = (v) => {
        if (typeof v !== 'number') return '–';
        const d = new Date(v);
        const hh = String(d.getHours()).padStart(2, '0');
        const mm = String(d.getMinutes()).padStart(2, '0');
        const ss = String(d.getSeconds()).padStart(2, '0');
        const ms = String(d.getMilliseconds()).padStart(3, '0');
        return `${hh}:${mm}:${ss}.${ms}`;
    };

    if (isModal) {
        const parseLatency = (Number.isFinite(srchStart) && Number.isFinite(recv)) ? (srchStart - recv) : undefined;
        const putLatency = (Number.isFinite(t.ivs_put_start_ms) && Number.isFinite(t.lam_build_done_ms)) ? (t.ivs_put_start_ms - t.lam_build_done_ms) : undefined;
        const events = [
            { label: 'Detection Triggered', time: ts, color: 'bg-blue-500' },
            { label: 'Lambda Build Done', time: lamDone, color: 'bg-blue-400', extra: Number.isFinite(lamExec) ? `build ${fmtMsAndSec(lamExec)}` : undefined },
            { label: 'Metadata Sent', time: sent, color: 'bg-green-500', extra: Number.isFinite(putLatency) ? `put ${fmtMsAndSec(putLatency)}` : undefined },
            { label: 'Metadata Arrived', time: recv, color: 'bg-cyan-500', extra: Number.isFinite(svToCl) ? `net ${fmtMsAndSec(svToCl)}` : undefined },
            { label: 'Search Started', time: srchStart, color: 'bg-amber-500', extra: Number.isFinite(parseLatency) ? `parse ${fmtMsAndSec(parseLatency)}` : undefined },
            { label: 'Search Finished', time: srchEnd, color: 'bg-amber-400', extra: Number.isFinite(srchLatency) ? `search ${fmtMsAndSec(srchLatency)}` : undefined },
            { label: 'Shown to User', time: shown, color: 'bg-purple-500', extra: Number.isFinite(clToShown) ? `client ${fmtMsAndSec(clToShown)}` : undefined },
        ].filter(e => typeof e.time === 'number');

        return (
            <div className="space-y-4">
                <div className="text-sm font-semibold text-[#4cc3e9] mb-3">Timing Analysis</div>

                {/* Timeline */}
                <div className="space-y-2">
                    {events.map((event, idx) => (
                        <div key={idx}>
                            <div className="flex items-center gap-3 p-2 rounded-lg bg-gradient-to-r from-[#1a2448]/40 to-transparent border-l-2 border-[#4cc3e9]/50">
                                <div className={`w-3 h-3 rounded-full ${event.color} shadow-lg`} />
                                <div className="flex-1">
                                    <div className="text-xs font-medium text-white/90">{event.label}</div>
                                    <div className="text-[10px] text-[#4cc3e9]/80">{event.time ? fmtTime(event.time) : '–'}{event.extra ? ` • ${event.extra}` : ''}</div>
                                </div>
                            </div>

                            {/* Show step duration between events */}
                            {idx < events.length - 1 && (
                                <div className="flex items-center justify-center py-1">
                                    <div className="bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent h-px w-8" />
                                    <div className="mx-2 px-2 py-1 bg-yellow-400/10 border border-yellow-400/30 rounded text-[10px] text-yellow-400 font-medium">
                                        {(() => {
                                            const a = events[idx]?.time; const b = events[idx + 1]?.time;
                                            if (typeof a === 'number' && typeof b === 'number') return fmtMsAndSec(b - a);
                                            return '–';
                                        })()}
                                    </div>
                                    <div className="bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent h-px w-8" />
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-1 gap-2 text-[11px]">
                    <div className="bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 rounded-lg px-3 py-2"><div className="text-[#4cc3e9]/80 uppercase tracking-wide font-medium">Lambda Build</div><div className="text-white font-semibold">{fmtMsAndSec(lamExec)}</div></div>
                    <div className="bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 rounded-lg px-3 py-2"><div className="text-[#4cc3e9]/80 uppercase tracking-wide font-medium">Index Put</div><div className="text-white font-semibold">{fmtMsAndSec((Number.isFinite(t.ivs_put_start_ms) && Number.isFinite(t.lam_build_done_ms)) ? (t.ivs_put_start_ms - t.lam_build_done_ms) : undefined)}</div></div>
                    <div className="bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 rounded-lg px-3 py-2"><div className="text-[#4cc3e9]/80 uppercase tracking-wide font-medium">Metadata Network</div><div className="text-white font-semibold">{fmtMsAndSec(svToCl)}</div></div>
                    <div className="bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 rounded-lg px-3 py-2"><div className="text-[#4cc3e9]/80 uppercase tracking-wide font-medium">Parsing</div><div className="text-white font-semibold">{fmtMsAndSec((Number.isFinite(srchStart) && Number.isFinite(recv)) ? (srchStart - recv) : undefined)}</div></div>
                    <div className="bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 rounded-lg px-3 py-2"><div className="text-[#4cc3e9]/80 uppercase tracking-wide font-medium">Search</div><div className="text-white font-semibold">{fmtMsAndSec(srchLatency)}</div></div>
                    <div className="bg-gradient-to-r from-[#7b3ff2]/20 to-[#9060ff]/20 border border-[#9060ff]/30 rounded-lg px-3 py-2">
                        <div className="text-[#9060ff] uppercase tracking-wide font-medium">Total Duration</div>
                        <div className="text-white font-bold">{fmtMsAndSec(totalDuration)}</div>
                    </div>
                </div>
            </div>
        );
    }

    // Card view: compact display
    return (
        <div className="mt-2 space-y-1 text-[10px]">
            <div className="text-[#4cc3e9]/80 uppercase tracking-wide text-[9px] font-medium">Timing (chronological)</div>
            <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Detection Triggered</div>
                <div className="text-white/90">{fmtTime(ts)}</div>
            </div>

            {/* Step duration: Lambda → Metadata Sent */}
            {typeof metadataSentLatency === 'number' && (
                <div className="pl-4 py-0.5">
                    <div className="text-yellow-400 text-[9px] font-medium">↓ {fmtMsAndSec(metadataSentLatency)}</div>
                </div>
            )}

            <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Metadata Sent</div>
                <div className="text-white/90">{fmtTime(sent)}</div>
            </div>

            {/* Step duration: Metadata Sent → Arrived */}
            {typeof svToCl === 'number' && (
                <div className="pl-4 py-0.5">
                    <div className="text-green-400 text-[9px] font-medium">↓ {fmtMsAndSec(svToCl)}</div>
                </div>
            )}

            <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Arrived (Client)</div>
                <div className="text-white/90">{fmtTime(recv)}</div>
            </div>

            {/* Step duration: Arrived → Shown */}
            {typeof clToShown === 'number' && (
                <div className="pl-4 py-0.5">
                    <div className="text-green-400 text-[9px] font-medium">↓ {fmtMsAndSec(clToShown)}</div>
                </div>
            )}

            <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Shown</div>
                <div className="text-white/90">{fmtTime(shown)}</div>
            </div>

            {/* Removed explicit Search Completed time; keep only Search latency */}

            <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Detection Exec Duration</div>
                <div className="text-white/90">{fmtMsAndSec(dur)}</div>
            </div>

            {typeof srchLatency === 'number' && (
                <div className="bg-gradient-to-r from-[#1a2448]/40 to-[#1a2448]/20 border border-[#4cc3e9]/10 rounded-lg px-2 py-1">
                    <div className="uppercase tracking-wide text-[#4cc3e9]/70 font-medium">Search Latency</div>
                    <div className="text-white/90">{fmtMsAndSec(srchLatency)}</div>
                </div>
            )}

            <div className="bg-gradient-to-r from-[#7b3ff2]/20 to-[#9060ff]/20 border border-[#9060ff]/20 rounded-lg px-2 py-1">
                <div className="uppercase tracking-wide text-[#9060ff]/80 font-medium">Total</div>
                <div className="font-semibold text-white">{fmtMsAndSec(totalDuration)}</div>
            </div>
        </div>
    );
}

function FalseDetectionManager({ detection, customLabels, onUpdate, isCompact = false }) {
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);
    const [showLabelForm, setShowLabelForm] = useState(false);
    const [selectedFalseLabel, setSelectedFalseLabel] = useState('');
    const [trueLabelInput, setTrueLabelInput] = useState('');

    const hasDetails = detection.false_detection_details &&
        (detection.false_detection_details.false_label || detection.false_detection_details.true_label);

    const handleClearFalseDetection = async () => {
        setIsUpdating(true);
        setError(null);
        try {
            const updatedDetection = await markLabelAsFalse(detection.id, null, null);
            onUpdate(updatedDetection);
            setShowLabelForm(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleMarkLabelFalse = async () => {
        if (!selectedFalseLabel.trim()) {
            setError('Please select a false label');
            return;
        }

        setIsUpdating(true);
        setError(null);
        try {
            const updatedDetection = await markLabelAsFalse(
                detection.id,
                selectedFalseLabel.trim(),
                trueLabelInput.trim() || null
            );
            onUpdate(updatedDetection);
            setShowLabelForm(false);
            setSelectedFalseLabel('');
            setTrueLabelInput('');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsUpdating(false);
        }
    };

    if (isCompact) {
        return (
            <div className="flex items-center gap-2">
                {hasDetails ? (
                    <button
                        type="button"
                        onClick={handleClearFalseDetection}
                        disabled={isUpdating}
                        className="px-2 py-1 rounded text-xs bg-red-500/80 border border-red-400 text-white hover:bg-red-500 disabled:opacity-50"
                        title="Clear false detection"
                    >
                        {isUpdating ? '...' : '❌ Clear'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowLabelForm(true)}
                        className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20 text-white hover:bg-white/20"
                        title="Mark label as false"
                    >
                        Mark False
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Status Display */}
            {hasDetails && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div className="text-sm font-medium text-red-300 mb-2">False Detection Details</div>
                    <div className="space-y-1 text-xs">
                        {detection.false_detection_details.false_label && (
                            <div><span className="text-red-300">False Label:</span> <span className="text-white">{detection.false_detection_details.false_label}</span></div>
                        )}
                        {detection.false_detection_details.true_label && (
                            <div><span className="text-green-300">Correct Label:</span> <span className="text-white">{detection.false_detection_details.true_label}</span></div>
                        )}
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
                {hasDetails ? (
                    <button
                        type="button"
                        onClick={handleClearFalseDetection}
                        disabled={isUpdating}
                        className="px-3 py-1.5 rounded text-xs font-medium bg-red-500/80 border border-red-400 text-white hover:bg-red-500 disabled:opacity-50"
                    >
                        {isUpdating ? 'Clearing...' : 'Clear False Detection'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setShowLabelForm(true)}
                        disabled={isUpdating}
                        className="px-3 py-1.5 rounded text-xs font-medium bg-white/10 border border-white/20 text-white hover:bg-white/20 disabled:opacity-50"
                    >
                        Mark Label as False
                    </button>
                )}
            </div>

            {/* Label Selection Form */}
            {showLabelForm && (
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3">
                    <div className="text-sm font-medium text-white">Mark Specific Label as False</div>

                    <div>
                        <label className="text-xs text-white/80 block mb-1">False Label</label>
                        <select
                            value={selectedFalseLabel}
                            onChange={(e) => setSelectedFalseLabel(e.target.value)}
                            className="w-full px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-xs"
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                color: 'white'
                            }}
                        >
                            <option value="" style={{ backgroundColor: '#1f2937', color: 'white' }}>
                                Select a label to mark as false...
                            </option>
                            {customLabels.map((label, idx) => (
                                <option
                                    key={idx}
                                    value={label.name}
                                    style={{ backgroundColor: '#1f2937', color: 'white' }}
                                >
                                    {label.name} ({label.type})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="text-xs text-white/80 block mb-1">Correct Label (optional)</label>
                        <input
                            type="text"
                            value={trueLabelInput}
                            onChange={(e) => setTrueLabelInput(e.target.value)}
                            placeholder="What should it be instead?"
                            className="w-full px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-xs placeholder-white/50"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleMarkLabelFalse}
                            disabled={isUpdating || !selectedFalseLabel.trim()}
                            className="px-3 py-1 rounded text-xs bg-red-500/80 border border-red-400 text-white hover:bg-red-500 disabled:opacity-50"
                        >
                            {isUpdating ? 'Updating...' : 'Mark as False'}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowLabelForm(false);
                                setSelectedFalseLabel('');
                                setTrueLabelInput('');
                                setError(null);
                            }}
                            className="px-3 py-1 rounded text-xs bg-white/10 border border-white/20 text-white hover:bg-white/20"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded px-2 py-1">
                    {error}
                </div>
            )}
        </div>
    );
}

// Legacy component for backward compatibility
function FalseDetectionToggle({ detection, onUpdate }) {
    return <FalseDetectionManager detection={detection} customLabels={[]} onUpdate={onUpdate} isCompact={true} />;
}

function DetectionCard({ det, allDetections = [], currentIndex = 0, onNavigate }) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentDet, setCurrentDet] = useState(det);
    const [activeIndex, setActiveIndex] = useState(currentIndex);

    // Update currentDet when det prop changes
    useEffect(() => {
        setCurrentDet(det);
        setActiveIndex(currentIndex);
    }, [det, currentIndex]);

    // Reset activeIndex when modal opens
    useEffect(() => {
        if (isOpen) {
            setActiveIndex(currentIndex);
        }
    }, [isOpen, currentIndex]);

    // Keyboard navigation for modal
    useEffect(() => {
        if (!isOpen || allDetections.length <= 1) return;
        
        const handleKeyPress = (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const prevIndex = activeIndex > 0 ? activeIndex - 1 : allDetections.length - 1;
                const prevDet = allDetections[prevIndex];
                if (prevDet) {
                    setCurrentDet(prevDet);
                    setActiveIndex(prevIndex);
                    onNavigate && onNavigate(prevIndex);
                }
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                const nextIndex = activeIndex < allDetections.length - 1 ? activeIndex + 1 : 0;
                const nextDet = allDetections[nextIndex];
                if (nextDet) {
                    setCurrentDet(nextDet);
                    setActiveIndex(nextIndex);
                    onNavigate && onNavigate(nextIndex);
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [isOpen, activeIndex, allDetections, onNavigate]);

    const handleDetectionUpdate = (updatedDetection) => {
        setCurrentDet(prev => ({
            ...prev,
            false_detection_details: updatedDetection.false_detection_details
        }));
    };

    const customLabels = useMemo(() => {
        const meta = currentDet?.raw || {};
        return extractCustomLabels(meta);
    }, [currentDet]);

    const teams = useMemo(() => {
        const meta = currentDet?.raw || {};
        const arr = Array.isArray(meta.teams) ? meta.teams : [];
        const items = [];
        arr.forEach((t) => {
            const teamLabel = t?.t || t?.team || '';
            const instList = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
            if (instList.length > 0) {
                instList.forEach((inst) => {
                    const jerseyConf = typeof inst?.cj === 'number' ? inst.cj : (typeof inst?.jersey?.confidence === 'number' ? inst.jersey.confidence : undefined);
                    const jerseyBox = Array.isArray(inst?.bj) && inst.bj.length === 4
                        ? inst.bj
                        : (Array.isArray(inst?.jersey?.box_rel) && inst.jersey.box_rel.length === 4 ? inst.jersey.box_rel : undefined);
                    const playerName = inst?.p || inst?.player || '';
                    const playerSim = typeof inst?.sp === 'number' ? inst.sp : (typeof inst?.player?.similarity === 'number' ? inst.player.similarity : undefined);
                    const playerBox = Array.isArray(inst?.bp) && inst.bp.length === 4
                        ? inst.bp
                        : (Array.isArray(inst?.player?.box_rel) && inst.player.box_rel.length === 4 ? inst.player.box_rel : undefined);
                    let numbers = [];
                    const n = inst?.n;
                    if (n && typeof n === 'object' && !Array.isArray(n)) {
                        const text = n.t ?? n.text ?? n.value ?? '';
                        const conf = n.c ?? n.confidence;
                        const bbox = Array.isArray(n.b) && n.b.length === 4 ? n.b : undefined;
                        numbers = [{ t: String(text || ''), c: conf, b: bbox }];
                    } else if (Array.isArray(n)) {
                        numbers = n.map((v) => {
                            if (typeof v === 'string' || typeof v === 'number') return { t: String(v), c: undefined, b: undefined };
                            if (v && typeof v === 'object') {
                                const text = v.t ?? v.text ?? v.value ?? '';
                                const conf = v.c ?? v.confidence;
                                const bbox = Array.isArray(v.b) && v.b.length === 4 ? v.b : undefined;
                                return { t: String(text || ''), c: conf, b: bbox };
                            }
                            return { t: '', c: undefined, b: undefined };
                        });
                    }
                    items.push({ teamLabel, jerseyConf, jerseyBox, playerName, playerSim, playerBox, numbers });
                });
            } else {
                // Fallback to older flattened per-team shape
                const jerseyConf = typeof t?.tj === 'number' ? t.tj : undefined;
                const jerseyBox = Array.isArray(t?.bj) && t.bj.length === 4 ? t.bj : undefined;
                const playerName = t?.p || t?.player || '';
                const playerSim = typeof t?.sp === 'number' ? t.sp : undefined;
                const playerBox = Array.isArray(t?.bp) && t.bp.length === 4 ? t.bp : undefined;
                let numbers = [];
                if (Array.isArray(t?.n)) {
                    numbers = t.n.map((v) => {
                        if (typeof v === 'string' || typeof v === 'number') return { t: String(v), c: undefined, b: undefined };
                        if (v && typeof v === 'object') {
                            const text = v.t ?? v.text ?? '';
                            const conf = v.c ?? v.confidence;
                            const bbox = Array.isArray(v.b) && v.b.length === 4 ? v.b : undefined;
                            return { t: String(text || ''), c: conf, b: bbox };
                        }
                        return { t: '', c: undefined, b: undefined };
                    });
                }
                items.push({ teamLabel, jerseyConf, jerseyBox, playerName, playerSim, playerBox, numbers });
            }
        });
        // Also support generic 'labels' array (e.g., series/product labels) with bbox 'b'
        const labels = Array.isArray(meta.labels) ? meta.labels : [];
        labels.forEach((l) => {
            const labelName = l?.n || l?.name || '';
            const conf = typeof l?.c === 'number' ? l.c : (typeof l?.confidence === 'number' ? l.confidence : undefined);
            const bbox = Array.isArray(l?.b) && l.b.length === 4 ? l.b : undefined;
            const personName = l?.p || ''; // character/person name
            const personConf = typeof l?.sp === 'number' ? l.sp : undefined; // character confidence  
            const personBbox = Array.isArray(l?.bp) && l.bp.length === 4 ? l.bp : undefined; // character bounding box
            if (bbox) {
                items.push({ 
                    teamLabel: labelName, 
                    jerseyConf: conf, 
                    jerseyBox: bbox, 
                    playerName: personName, 
                    playerSim: personConf, 
                    playerBox: personBbox, 
                    numbers: [],
                    isSeriesLabel: true // flag to distinguish series labels
                });
            }
        });
        // For skipped frames where we might have only faces/unassigned_faces
        const faces = Array.isArray(meta.faces) ? meta.faces : [];
        const unassigned = Array.isArray(meta.unassigned_faces) ? meta.unassigned_faces : [];
        const keyForBox = (b) => Array.isArray(b) && b.length === 4 ? b.map(v => Number(v).toFixed(4)).join(',') : '';
        const unassignedKeys = new Set();
        // Prefer unassigned faces; when no name, mark as unlabeledFace to hide in details
        unassigned.forEach((f) => {
            const bbox = Array.isArray(f?.b) && f.b.length === 4 ? f.b : undefined;
            if (!bbox) return;
            const hasName = (typeof f?.p === 'string' && f.p);
            const name = hasName ? f.p : '';
            const sim = (typeof f?.sp === 'number') ? f.sp : undefined;
            unassignedKeys.add(keyForBox(bbox));
            items.push({ teamLabel: 'face', jerseyConf: undefined, jerseyBox: undefined, playerName: name, playerSim: sim, playerBox: bbox, numbers: [], unlabeledFace: !hasName });
        });
        // Do not render generic faces without names; skip to avoid duplicate/unnamed overlays
        return items;
    }, [currentDet]);

    const teamSummaries = useMemo(() => {
        // Build concise summaries by team with most common kit and players
        const byTeam = new Map();
        teams.forEach((it) => {
            const teamRaw = it.teamLabel || '';
            const tl = String(teamRaw).toLowerCase();
            // Skip face pseudo-teams
            if (tl === 'face' || tl === 'face?' || tl === 'unassigned face') return;
            const team = teamRaw;
            if (!byTeam.has(team)) byTeam.set(team, []);
            byTeam.get(team).push(it);
        });
        const summaries = [];
        byTeam.forEach((list, team) => {
            const variantCounts = {};
            let topVariant = '';
            let topCount = 0;
            list.forEach((it) => {
                // Try to infer variant from jersey label if present like "bayern home_kit_2025 back"
                const tokens = String(it.teamLabel || '').split(/\s+/);
                // Prefer an explicit variant in metadata if present on raw; fallback: look for token with '_kit_'
                const infer = tokens.find((tk) => tk.includes('_kit_')) || '';
                const v = infer;
                if (v) {
                    variantCounts[v] = (variantCounts[v] || 0) + 1;
                    if (variantCounts[v] > topCount) { topVariant = v; topCount = variantCounts[v]; }
                }
            });
            const players = list
                .map((it) => ({
                    name: it.playerName,
                    number: (Array.isArray(it.numbers) && it.numbers[0]?.t) ? it.numbers[0].t : undefined,
                    jerseyConf: it.jerseyConf,
                    numberConf: (Array.isArray(it.numbers) && typeof it.numbers[0]?.c === 'number') ? it.numbers[0].c : undefined,
                    sim: it.playerSim,
                }))
                .filter((p) => !!p.name || !!p.number);
            summaries.push({ team, kit: topVariant, players });
        });
        // Do not show groups that would only list unnamed faces (no players)
        return summaries.filter((s) => Array.isArray(s.players) && s.players.length > 0);
    }, [teams]);

    // Helper function to check if a label is marked as false
    const isLabelMarkedFalse = (labelName) => {
        const falseLabel = currentDet?.false_detection_details?.false_label;
        return falseLabel && labelName && String(labelName).trim() === String(falseLabel).trim();
    };

    const Box = ({ box, color = 'border-yellow-400', label, withLabel = true, labelBgClass, labelTextClass = 'text-black', labelOffsetPx = 0, isFalseLabel = false }) => {
        if (!box || box.length !== 4) return null;
        const [L, T, W, H] = box;
        const left = Math.max(0, Math.min(100, L * 100));
        const top = Math.max(0, Math.min(100, T * 100));
        const width = Math.max(0, Math.min(100, W * 100));
        const height = Math.max(0, Math.min(100, H * 100));
        const placeRight = left + width < 80; // if too close to right edge, place label on the left side

        // Override colors for false labels
        const finalColor = isFalseLabel ? 'border-red-500' : color;
        const finalBgClass = isFalseLabel ? 'bg-red-500' : (labelBgClass || color.replace('border-', 'bg-'));
        const finalTextClass = isFalseLabel ? 'text-white' : labelTextClass;

        return (
            <div className={`absolute ${finalColor}`} style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}>
                {withLabel && (
                    <div className={`absolute ${placeRight ? 'left-full ml-2' : 'right-full mr-2'} px-1.5 py-0.5 rounded text-[10px] font-semibold ${finalTextClass} ${finalBgClass} shadow ${isFalseLabel ? 'animate-pulse' : ''}`} style={{ top: `${-labelOffsetPx}px` }}>
                        {isFalseLabel && '❌ '}{label}
                    </div>
                )}
                <div className={`w-full h-full border-2 ${finalColor} ${isFalseLabel ? 'animate-pulse border-4' : ''}`} />
            </div>
        );
    };

    const SkippedDetails = ({ det }) => {
        const meta = det?.raw || {};
        const reason = det?.reason || meta?.reason || 'skipped';
        const faces = Array.isArray(meta?.faces) ? meta.faces : [];
        const unFaces = Array.isArray(meta?.unassigned_faces) ? meta.unassigned_faces : [];
        const teamsCount = Array.isArray(meta?.teams) ? meta.teams.length : 0;
        const jerseys = Array.isArray(meta?.jerseys) ? meta.jerseys : [];
        const keyForBox = (b) => Array.isArray(b) && b.length === 4 ? b.map(v => Number(v).toFixed(4)).join(',') : '';
        const faceKeySet = new Set();
        unFaces.forEach(f => { const k = keyForBox(f?.b); if (k) faceKeySet.add(k); });
        faces.forEach(f => { const k = keyForBox(f?.b); if (k && !faceKeySet.has(k)) faceKeySet.add(k); });
        const facesTotal = faceKeySet.size;

        // Build simple lists for display
        const rawTeamLabels = (Array.isArray(meta?.teams) ? meta.teams : [])
            .map(t => (t && (t.t || t.team)) || '')
            .filter(Boolean);
        const teamLabels = Array.from(new Set(rawTeamLabels.map(v => String(v).trim().toLowerCase())))
            .filter(v => v && v !== 'face' && v !== 'unassigned face')
            .map(v => v);
        const rawJerseys = jerseys.map(j => (j && (j.n || j.name)) || '').filter(Boolean);
        const jerseyNames = Array.from(new Set(rawJerseys.map(v => String(v))))
            .map(v => v);
        const namedFaces = unFaces.filter(f => typeof f?.p === 'string' && f.p);
        // Count anonymous unassigned faces (dedup by bbox)
        const anonFaceKeys = new Set();
        unFaces.forEach(f => { if (!(typeof f?.p === 'string' && f.p)) { const k = keyForBox(f?.b); if (k) anonFaceKeys.add(k); } });
        const anonUnassignedCount = anonFaceKeys.size;
        // Extract product/series labels for this frame (series detection)
        const labelItems = Array.isArray(meta?.labels) ? meta.labels : [];
        const hasLabels = labelItems.length > 0;

        return (
            <div className="mt-2 grid grid-cols-1 gap-2 text-[12px]">
                <div className="rounded-lg bg-amber-500/10 border border-amber-400/30 px-3 py-2 text-amber-200">
                    <div className="font-semibold">Skipped</div>
                    <div className="text-[11px]">Reason: <span className="text-white/90">{String(reason)}</span></div>
                </div>
                {hasLabels && (
                    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                        <div className="font-semibold text-white/80 mb-1">Detected labels</div>
                        <ul className="space-y-1">
                            {labelItems.map((l, i) => (
                                <li key={`lbl-${i}`} className="flex items-center gap-2 text-white/90">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#4cc3e9]" />
                                    <span className="truncate" title={l?.n || l?.name || ''}>{l?.n || l?.name || ''}</span>
                                    {typeof l?.c === 'number' && (
                                        <span className="ml-auto text-[10px] text-white/60">{l.c.toFixed(1)}%</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                <div className="rounded-lg bg-gradient-to-r from-[#1a2448]/60 to-[#1a2448]/40 border border-[#4cc3e9]/20 px-3 py-2">
                    <div className="text-[#4cc3e9]/80 uppercase tracking-wide text-[10px] mb-1">Counts</div>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-white/90">
                        <div>Teams: <span className="text-white/80">{teamsCount}</span></div>
                        <div>Jerseys: <span className="text-white/80">{jerseys.length}</span></div>
                        <div>Faces: <span className="text-white/80">{facesTotal}</span></div>
                    </div>
                </div>
                {(teamLabels.length > 0) && (
                    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                        <div className="text-[#4cc3e9]/80 uppercase tracking-wide text-[10px] mb-1">Teams</div>
                        <ul className="list-disc list-inside text-white/90 text-[12px]">
                            {teamLabels.map((nm, i) => (<li key={`t-${i}`} className="capitalize">{nm}</li>))}
                        </ul>
                    </div>
                )}
                {(jerseyNames.length > 0) && (
                    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                        <div className="text-[#4cc3e9]/80 uppercase tracking-wide text-[10px] mb-1">Jerseys</div>
                        <ul className="list-disc list-inside text-white/90 text-[12px]">
                            {jerseyNames.map((nm, i) => (<li key={`j-${i}`}>{nm.replace(/_/g, ' ')}</li>))}
                        </ul>
                    </div>
                )}
                {((namedFaces.length > 0) || (anonUnassignedCount > 0)) && (
                    <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                        <div className="text-[#4cc3e9]/80 uppercase tracking-wide text-[10px] mb-1">Faces</div>
                        <div className="space-y-1 text-white/90 text-[12px]">
                            {namedFaces.map((f, idx) => (
                                <div key={`nf-${idx}`} className="flex items-center gap-2">
                                    <span className="capitalize">{String(f.p)}</span>
                                    {typeof f.sp === 'number' && (
                                        <span className="text-white/60 text-[11px]">{f.sp.toFixed(1)}%</span>
                                    )}
                                </div>
                            ))}
                            {anonUnassignedCount > 0 && (
                                <div className="text-white/80">Unassigned faces: <span className="font-semibold">{anonUnassignedCount}</span></div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="rounded-xl overflow-hidden border border-[#4cc3e9]/20 bg-gradient-to-br from-[#1a2448]/60 to-[#1a2448]/40 hover:from-[#1a2448]/80 hover:to-[#1a2448]/60 transition-all duration-200 hover:scale-[1.02] hover:border-[#4cc3e9]/40 transform-gpu will-change-transform">
                <div
                    className="relative aspect-[16/9] bg-black/50 flex items-center justify-center overflow-hidden cursor-pointer"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsOpen(true);
                    }}
                >
                    {currentDet.imgUrl ? (
                        <img src={currentDet.imgUrl} alt="thumb" className="w-full h-full object-cover" />
                    ) : (
                        <div className="text-white/40 text-xs p-3">No image</div>
                    )}
                    {/* Status tag */}
                    <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
                        {currentDet.skipped ? (
                            <span className="px-2 py-1 rounded bg-amber-500/80 text-black text-[10px] font-bold border border-amber-300/60">
                                Skipped{currentDet.reason ? `: ${currentDet.reason}` : ''}
                            </span>
                        ) : (
                            <span className="px-2 py-1 rounded bg-emerald-500/80 text-black text-[10px] font-bold border border-emerald-300/60">
                                Detected
                            </span>
                        )}
                        {currentDet.false_detection_details && (
                            <span className="px-2 py-1 rounded bg-red-500/90 text-white text-[10px] font-bold border border-red-400/60">
                                ❌ FALSE LABEL
                            </span>
                        )}
                    </div>
                    {/* Detection ID tag */}
                    <div className="absolute top-2 right-2 z-10">
                        <span className="px-2 py-1 rounded bg-white/80 text-black text-[10px] font-semibold border border-white/60">
                            ID: {String(currentDet.id)}
                        </span>
                    </div>
                    <div className="absolute inset-0 pointer-events-none">
                        {teams.map((t, idx) => {
                            // Check if this team label is marked as false
                            const teamLabel = t.teamLabel || 'team';
                            const isTeamLabelFalse = isLabelMarkedFalse(teamLabel);

                            return (
                                <React.Fragment key={idx}>
                                    <Box
                                        box={t.jerseyBox}
                                        color="border-yellow-400"
                                        label={`${teamLabel}${typeof t.jerseyConf === 'number' ? ` ${t.jerseyConf.toFixed(1)}%` : ''}`}
                                        withLabel={false}
                                        isFalseLabel={isTeamLabelFalse}
                                    />
                                    <Box
                                        box={t.playerBox}
                                        color="border-cyan-400"
                                        label={`${t.playerName || (t.isSeriesLabel ? 'character' : 'player')}${typeof t.playerSim === 'number' ? ` ${t.playerSim.toFixed(1)}%` : ''}`}
                                        withLabel={false}
                                    />
                                    {Array.isArray(t.numbers) && t.numbers.map((n, i) => (
                                        <Box
                                            key={i}
                                            box={n.b}
                                            color="border-fuchsia-400"
                                            label={`${n.t ? `#${n.t}` : 'number'}${typeof n.c === 'number' ? ` ${n.c.toFixed(1)}%` : ''}`}
                                            withLabel={false}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
                <div className="p-3 text-sm">
                    <div className="truncate">
                        {currentDet.team && <span className="capitalize">{currentDet.team}</span>}
                        {currentDet.team && currentDet.player && <span className="mx-2 text-white/40">•</span>}
                        {currentDet.player && <span className="capitalize">{currentDet.player}</span>}
                        {typeof currentDet.number !== 'undefined' && currentDet.number !== null && currentDet.number !== '' && (
                            <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-semibold">#{currentDet.number}</span>
                        )}
                    </div>
                    <div className="mt-1 text-[10px] text-[#4cc3e9]/70">Click image to enlarge and show labels</div>
                    {currentDet.skipped ? <SkippedDetails det={currentDet} /> : <Timing det={currentDet} />}
                    <div className="mt-3 pt-2 border-t border-white/10 space-y-2">
                        <FalseDetectionManager
                            detection={currentDet}
                            customLabels={customLabels}
                            onUpdate={handleDetectionUpdate}
                            isCompact={true}
                        />
                        <AddToDataset
                            detectionId={currentDet.id}
                            mode="button"
                            compact={true}
                            className="flex justify-end"
                        />
                    </div>
                </div>
            </div>

            {/* Modal rendered outside card container to avoid overflow issues */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
                    onClick={() => setIsOpen(false)}
                    style={{
                        backdropFilter: 'blur(8px)',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <div
                        className="relative w-full max-w-7xl animate-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Navigation and Close buttons - moved inside modal */}
                        <div className="flex items-center justify-between mb-4 px-4 py-2 bg-black/50 rounded-t-xl">
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const prevIndex = activeIndex > 0 ? activeIndex - 1 : allDetections.length - 1;
                                        const prevDet = allDetections[prevIndex];
                                        if (prevDet) {
                                            setCurrentDet(prevDet);
                                            setActiveIndex(prevIndex);
                                            onNavigate && onNavigate(prevIndex);
                                        }
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#1a2448]/80 to-[#1a2448]/60 hover:from-[#1a2448] hover:to-[#1a2448] text-white text-sm font-medium border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 transition-all duration-200"
                                    title="Previous image"
                                >
                                    ← Prev
                                </button>
                                <span className="text-white/80 text-sm px-2">
                                    {activeIndex + 1} / {allDetections?.length || 1}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const nextIndex = activeIndex < allDetections.length - 1 ? activeIndex + 1 : 0;
                                        const nextDet = allDetections[nextIndex];
                                        if (nextDet) {
                                            setCurrentDet(nextDet);
                                            setActiveIndex(nextIndex);
                                            onNavigate && onNavigate(nextIndex);
                                        }
                                    }}
                                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#1a2448]/80 to-[#1a2448]/60 hover:from-[#1a2448] hover:to-[#1a2448] text-white text-sm font-medium border border-[#4cc3e9]/30 hover:border-[#4cc3e9]/50 transition-all duration-200"
                                    title="Next image"
                                >
                                    Next →
                                </button>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white text-sm font-medium border border-[#9060ff]/50 transition-all duration-200"
                            >
                                ✕ Close
                            </button>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Detection Image - Left Side */}
                            <div className="lg:col-span-2">
                                <div className="relative w-full aspect-[16/9] bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl overflow-hidden border border-[#4cc3e9]/30">
                                    {currentDet.imgUrl ? (
                                        <img
                                            src={currentDet.imgUrl}
                                            alt="Detection Analysis"
                                            className="w-full h-full object-cover"
                                            loading="eager"
                                        />
                                    ) : (
                                        <div className="flex items-center justify-center h-full text-[#4cc3e9]/60">
                                            No image available
                                        </div>
                                    )}
                                    <div className="absolute inset-0 pointer-events-none">
                                        {teams.map((t, idx) => {
                                            // Check if this team label is marked as false
                                            const teamLabel = t.teamLabel || 'team';
                                            const isTeamLabelFalse = isLabelMarkedFalse(teamLabel);

                                            return (
                                                <React.Fragment key={idx}>
                                                    <Box
                                                        box={t.jerseyBox}
                                                        color="border-yellow-400"
                                                        label={`${teamLabel}${typeof t.jerseyConf === 'number' ? ` ${t.jerseyConf.toFixed(1)}%` : ''}`}
                                                        withLabel={true}
                                                        isFalseLabel={isTeamLabelFalse}
                                                    />
                                                    <Box
                                                        box={t.playerBox}
                                                        color="border-cyan-400"
                                                        label={`${t.playerName || (t.isSeriesLabel ? 'character' : 'player')}${typeof t.playerSim === 'number' ? ` ${t.playerSim.toFixed(1)}%` : ''}`}
                                                        withLabel={Boolean(t.playerName)}
                                                        labelBgClass={t.isSeriesLabel ? "bg-purple-600" : "bg-blue-600"}
                                                        labelTextClass="text-white"
                                                        labelOffsetPx={14}
                                                    />
                                                    {Array.isArray(t.numbers) && t.numbers.map((n, i) => (
                                                        <Box
                                                            key={i}
                                                            box={n.b}
                                                            color="border-fuchsia-400"
                                                            label={`${n.t ? `#${n.t}` : 'number'}${typeof n.c === 'number' ? ` ${n.c.toFixed(1)}%` : ''}`}
                                                            withLabel={true}
                                                        />
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Right Side Panel: Show detections only (no timing in modal) */}
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-4 border border-[#4cc3e9]/30 max-h-[80vh] overflow-y-auto">
                                <div className="flex items-center justify-between mb-3 text-xs text-white/80">
                                    <div className="font-semibold">Details</div>
                                    <div className="px-2 py-0.5 rounded bg-white/10 border border-white/20">ID: <span className="font-mono text-white">{String(currentDet.id)}</span></div>
                                </div>

                                {/* False Detection Management in Modal */}
                                <div className="mb-4 p-3 rounded-lg bg-white/5 border border-white/10">
                                    <div className="text-xs font-medium text-white/80 mb-2">Detection Status</div>
                                    <FalseDetectionManager
                                        detection={currentDet}
                                        customLabels={customLabels}
                                        onUpdate={handleDetectionUpdate}
                                        isCompact={false}
                                    />
                                </div>

                                {/* Add to Dataset in Modal */}
                                <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-[#4cc3e9]/10 to-[#4cc3e9]/5 border border-[#4cc3e9]/30">
                                    <AddToDataset
                                        detectionId={currentDet.id}
                                        mode="full"
                                    />
                                </div>

                                {/* Custom Labels Section */}
                                {customLabels.length > 0 && (
                                    <div className="mb-4 p-3 rounded-lg bg-gradient-to-r from-[#4cc3e9]/10 to-[#4cc3e9]/5 border border-[#4cc3e9]/30">
                                        <div className="text-sm font-semibold text-[#4cc3e9] mb-3">Detected Labels</div>
                                        <div className="space-y-2">
                                            {customLabels.map((label, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10">
                                                    <div className="flex-1">
                                                        <div className="text-sm font-medium text-white">{label.name}</div>
                                                        {label.type === 'match' && (
                                                            <div className="text-xs text-white/60 mt-1">
                                                                {label.team && <span className="capitalize">{label.team}</span>}
                                                                {label.variant && <span className="ml-1">({label.variant.replace(/_/g, ' ')})</span>}
                                                                {label.side && <span className="ml-1">• {label.side}</span>}
                                                            </div>
                                                        )}
                                                        {label.type === 'series' && label.person && (
                                                            <div className="text-xs text-white/60 mt-1">
                                                                <span className="text-purple-300">Character:</span> <span className="font-medium">{label.person}</span>
                                                                {typeof label.personConfidence === 'number' && (
                                                                    <span className="ml-1 text-purple-200">({label.personConfidence}%)</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-xs">
                                                        {typeof label.confidence === 'number' && (
                                                            <span className="px-2 py-1 rounded bg-[#4cc3e9]/20 text-[#4cc3e9]">
                                                                {label.confidence.toFixed(1)}%
                                                            </span>
                                                        )}
                                                        <span className="px-2 py-1 rounded bg-white/10 text-white/70 uppercase text-[10px]">
                                                            {label.type}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {currentDet.skipped ? (
                                    <div>
                                        <div className="text-sm font-semibold text-amber-300 mb-3">Skipped Detection</div>
                                        <SkippedDetails det={currentDet} />
                                    </div>
                                ) : (
                                    <div className="mb-3 text-sm font-semibold text-[#4cc3e9]">Detections</div>
                                )}
                                {teamSummaries && teamSummaries.length > 0 && (
                                    <div className="mt-4">
                                        <div className="text-[#4cc3e9] font-semibold mb-2">Teams</div>
                                        <div className="grid grid-cols-1 gap-2">
                                            {teamSummaries.map((tm, ti) => (
                                                <div key={`ts-${ti}`} className="rounded-lg border border-[#4cc3e9]/20 bg-gradient-to-br from-[#1a2448]/60 to-[#1a2448]/40 p-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="text-sm font-semibold capitalize">{tm.team}</div>
                                                        {tm.kit ? (
                                                            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#4cc3e9]/20 text-[#4cc3e9] border border-[#4cc3e9]/30">{tm.kit.replace(/_/g, ' ')}</span>
                                                        ) : null}
                                                    </div>
                                                    <div className="mt-2 space-y-1">
                                                        {(tm.players || []).length > 0 ? (
                                                            tm.players.map((p, pi) => (
                                                                <div key={`p-${ti}-${pi}`} className="flex items-center justify-between text-[12px]">
                                                                    <div className="flex items-center gap-2">
                                                                        {p.name ? (<span className="capitalize font-semibold">{p.name}</span>) : null}
                                                                        {p.number ? (
                                                                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded border border-white/20 bg-white/10 text-[10px]">#{p.number}</span>
                                                                        ) : null}
                                                                    </div>
                                                                    <div className="flex items-center gap-2 text-[10px] text-white/70">
                                                                        {typeof p.sim === 'number' && p.name ? (<span title="Face similarity">face {p.sim.toFixed(1)}%</span>) : null}
                                                                        {typeof p.jerseyConf === 'number' ? (<span title="Jersey confidence">jersey {p.jerseyConf.toFixed(1)}%</span>) : null}
                                                                        {typeof p.numberConf === 'number' ? (<span title="Number confidence"># {p.numberConf.toFixed(1)}%</span>) : null}
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="text-white/60 text-[12px]">No players resolved</div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
